/**
 * <s-product-gallery> — main image track with a thumbnail strip.
 *
 * Thumbnail buttons use Invoker Commands: commandfor="<gallery id>" command="--select"
 * and data-index="<zero-based slide index>". Selecting scrolls the track to that slide;
 * scrolling the track (swipe) updates which thumbnail is marked current.
 *
 * Required markup:
 *   [data-gallery-track] — horizontal scroll-snap container of slides.
 *   [data-gallery-thumb][data-index] — thumbnail buttons.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('command', this.#handleCommand, opts);
    this.#track?.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #thumbs() {
    return Array.from(this.querySelectorAll('[data-gallery-thumb]'));
  }

  #handleCommand = (event) => {
    if (event.command !== '--select') return;
    const index = Number(event.source?.dataset.index ?? 0);
    const track = this.#track;
    const slide = track?.children[index];
    if (!track || !(slide instanceof HTMLElement)) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: index * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    this.#setCurrent(index);
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const track = this.#track;
      if (!track || track.clientWidth === 0) return;
      this.#setCurrent(Math.round(track.scrollLeft / track.clientWidth));
    });
  };

  #setCurrent(index) {
    for (const thumb of this.#thumbs) {
      const isCurrent = Number(thumb.dataset.index) === index;
      thumb.toggleAttribute('aria-current', isCurrent);
      if (isCurrent) thumb.setAttribute('aria-current', 'true');
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
